import { IStackManager } from 'almanac-ui/Modal/Types'
import { moveColumn, moveRow } from 'common/Prose/Commands/table/mutation'
import { TableMap } from 'common/Prose/utils/table/TableMap'
import { NotificationModal } from 'components/NotificationModal/NotificationModal'
import { isEqual, throttle } from 'lodash'
import { schema } from 'prose-core'
import { Plugin, PluginKey } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import { EDITOR_CONTAINER_ID, MAIN_CONTENT_ID } from 'screens/ProseEdit/Constants'
import styles from './TableDragManager.module.scss'

export const TableDragManagerPluginKey = new PluginKey('tableDragManager')

export type TableDragManagerState = {
  isDragging: boolean
  stackManager: IStackManager
  tablePos?: number
  startIdx?: number
  dragType?: 'row' | 'column'
  validDropPoint: boolean
  elementClone?: HTMLElement
  tableDOM?: HTMLTableElement
  dropPoint?: number
}

export type TableDragManagerMeta = {
  action: 'startDrag' | 'stopDrag' | 'setDropPoint'
  tablePos?: number
  startIdx?: number
  dragType?: 'row' | 'column'
  validDropPoint?: boolean
  elementClone?: HTMLElement
  tableDOM?: HTMLTableElement
  dropPoint?: number
}

const internalInitState = {
  isDragging: false,
  validDropPoint: false,
}

export const TableDragManagerPlugin = (stackManager: IStackManager) =>
  new Plugin<TableDragManagerState, any>({
    key: TableDragManagerPluginKey,
    state: {
      init(): TableDragManagerState {
        return {
          stackManager,
          ...internalInitState,
        }
      },
      apply(tr, pluginState, _oldState, _newState) {
        const meta: TableDragManagerMeta = tr.getMeta(TableDragManagerPluginKey)
        if (!meta) {
          if (pluginState.dropPoint && pluginState.tablePos) {
            return {
              ...pluginState,
              tablePos: tr.mapping.map(pluginState.tablePos),
              dropPoint: tr.mapping.map(pluginState.dropPoint),
            }
          }
          return pluginState
        }
        switch (meta.action) {
          case 'startDrag':
            document.documentElement.classList.remove(styles.dragging)
            return {
              ...pluginState,
              isDragging: true,
              tablePos: meta.tablePos,
              dragType: meta.dragType,
              startIdx: meta.startIdx,
              elementClone: meta.elementClone,
            }
          case 'stopDrag':
            document.documentElement.classList.remove(styles.dragging)
            return { stackManager, ...internalInitState }
          case 'setDropPoint':
            return {
              ...pluginState,
              dropPoint: meta.dropPoint,
              validDropPoint: meta.validDropPoint ?? pluginState.validDropPoint,
              tableDOM: meta.tableDOM ?? pluginState.tableDOM,
              elementClone: meta.elementClone ?? pluginState.elementClone,
            }
        }
      },
    },
    view: (view) => {
      return {
        destroy: () => {
          document.documentElement.classList.remove(styles.dragging)
        },
      }
    },
    props: {
      decorations: (state) => {
        const { isDragging, tablePos, tableDOM, dropPoint, dragType, elementClone } =
          TableDragManagerPluginKey.getState(state) as TableDragManagerState
        if (
          !isDragging ||
          tablePos === undefined ||
          tableDOM === undefined ||
          dropPoint === undefined ||
          elementClone === undefined
        ) {
          return DecorationSet.empty
        }

        let decoList = [Decoration.widget(0, elementClone, { key: 'tableDragMouseGhost' })]
        const table = state.doc.nodeAt(tablePos)
        if (!table || table.type !== schema.nodes.table) return DecorationSet.empty
        const tableMap = new TableMap({ posBeforeTable: tablePos, node: table })
        if (dragType === 'row') {
          decoList.push(
            Decoration.widget(tablePos, createTableRowDropPoint(tableDOM, dropPoint, tableMap), {
              key: `tableRowDropPoint-${dropPoint}`,
            }),
          )
        } else {
          decoList.push(
            Decoration.widget(tablePos, createTableColumnDropPoint(tableDOM, dropPoint, tableMap), {
              key: `tableColumnDropPoint-${dropPoint}`,
            }),
          )
        }

        return DecorationSet.create(state.doc, decoList)
      },
      handleDOMEvents: {
        mouseup: (view: EditorView, event: Event) => {
          const mouseEvent = event as MouseEvent
          const pluginState = TableDragManagerPluginKey.getState(view.state)
          if (pluginState.isDragging) {
            mouseEvent.preventDefault()
            handleDragEnd(view)
            return true
          }
          return false
        },

        mousemove: handleMouseMove,
      },
    },
  })

function handleDragEnd(view: EditorView) {
  const pluginState = TableDragManagerPluginKey.getState(view.state)
  if (!pluginState.isDragging || pluginState.tablePos === undefined) return
  if (pluginState.dropPoint !== undefined && pluginState.startIdx !== undefined) {
    const { startIdx, dropPoint } = pluginState
    const newIndex = dropPoint > startIdx ? dropPoint - 1 : dropPoint

    if (newIndex !== startIdx) {
      if (pluginState.dragType === 'row') {
        if (pluginState.validDropPoint) {
          const didMove = moveRow(startIdx, dropPoint, pluginState.tablePos)(view.state, view.dispatch)
          if (!didMove) {
            pluginState.stackManager.openModal(
              NotificationModal,
              {
                title: 'There was a problem',
                message: 'You cannot move a row with vertically merged cells.',
              },
              { newStack: false },
            )
          }
        } else {
          pluginState.stackManager.openModal(
            NotificationModal,
            {
              title: 'There was a problem',
              message: 'You cannot move a row to that location.',
            },
            { newStack: false },
          )
        }
      } else {
        if (pluginState.validDropPoint) {
          const didMove = moveColumn(startIdx, dropPoint, pluginState.tablePos)(view.state, view.dispatch)
          if (!didMove) {
            pluginState.stackManager.openModal(
              NotificationModal,
              {
                title: 'There was a problem',
                message: 'You cannot move a column with horizontally merged cells.',
              },
              { newStack: false },
            )
          }
        } else {
          pluginState.stackManager.openModal(
            NotificationModal,
            {
              title: 'There was a problem',
              message: 'You cannot move a column to that location.',
            },
            { newStack: false },
          )
        }
      }
    }
  }
  view.dispatch(
    view.state.tr.setMeta(TableDragManagerPluginKey, {
      action: 'stopDrag',
    }),
  )
}

function handleMouseMove(view: EditorView, event: MouseEvent) {
  const pluginState = TableDragManagerPluginKey.getState(view.state)
  if (!pluginState.isDragging) return false

  event.preventDefault()
  let dragClone: HTMLElement | null
  if (event.buttons === 0) {
    handleDragEnd(view)
  } else {
    handleDragMove(view, event)
  }
  if ((dragClone = document.getElementById('tableDragClone'))) {
    const { x, y } = event
    const { x: PM_x, y: PM_y } = view.dom?.getBoundingClientRect()

    dragClone.style.transform = `translate(${x - PM_x}px,${y - PM_y}px)`
  }
  return true
}

const handleDragMove = throttle(
  (view: EditorView, event: MouseEvent) => {
    const {
      state: { tr, doc },
      dispatch,
    } = view
    const pluginState = TableDragManagerPluginKey.getState(view.state)
    if (!pluginState.isDragging || pluginState.tablePos === undefined) return
    const tablePos = tr.mapping.map(pluginState.tablePos)
    const tableUnderDrag = doc.nodeAt(tablePos)
    if (!tableUnderDrag || tableUnderDrag.type !== schema.nodes.table) return
    const tableNodeDOMElement = (
      view.domAtPos(tr.mapping.map(pluginState.tablePos) + 1, 1).node as HTMLElement
    ).closest('[data-node-type="table"]')
    const table = tableNodeDOMElement?.querySelector('table')
    if (!table) return
    const tableMap = new TableMap({ posBeforeTable: tablePos, node: tableUnderDrag })

    if (!view.dom.lastElementChild) return
    let newDropPoint = pluginState.dropPoint
    let validDropPoint = false

    if (pluginState.dragType === 'row') {
      // Calculate closest drop point
      const possibleDropPoints = new Set(
        Array.from(table.querySelectorAll('tr'))
          .flatMap((tr) => {
            const rect = tr.getBoundingClientRect()
            const top = rect.top
            const bottom = rect.bottom
            return [top, bottom]
          })
          .sort((a, b) => a - b),
      )
      const closestDropPoint = Array.from(possibleDropPoints).reduce((a, b) => {
        return Math.abs(b - event.clientY) < Math.abs(a - event.clientY) ? b : a
      })
      newDropPoint = Array.from(possibleDropPoints).indexOf(closestDropPoint)

      // Check if drop point is valid
      const dropPointRowFragments = tableMap.getRowFragments()[newDropPoint]
      const dropPointSpansTable =
        dropPointRowFragments.length === 1 && isEqual(dropPointRowFragments[0], [0, tableMap.numCols - 1])
      const dropPointNotAboveHeader =
        tr.doc.resolve(
          tableMap.getOrderedPosListInRow(newDropPoint > pluginState.startIdx ? newDropPoint - 1 : newDropPoint)[0],
        ).nodeAfter?.type !== schema.nodes.table_header
      validDropPoint = dropPointSpansTable && dropPointNotAboveHeader
    } else {
      // Calculate closest drop point
      const possibleDropPoints = new Set(
        Array.from(table.querySelectorAll('th, td'))
          .flatMap((cell) => {
            const rect = cell.getBoundingClientRect()
            const left = rect.left
            const right = rect.right
            return [left, right]
          })
          .sort((a, b) => a - b),
      )
      const closestDropPoint = Array.from(possibleDropPoints).reduce((a, b) => {
        return Math.abs(b - event.clientX) < Math.abs(a - event.clientX) ? b : a
      })
      newDropPoint = Array.from(possibleDropPoints).indexOf(closestDropPoint)

      // Check if drop point is valid
      const dropPointColFragments = tableMap.getColFragments()[newDropPoint]
      validDropPoint =
        dropPointColFragments.length === 1 && isEqual(dropPointColFragments[0], [0, tableMap.numRows - 1])
    }

    const meta: TableDragManagerMeta = {
      action: 'setDropPoint',
      dropPoint: newDropPoint,
      validDropPoint,
      tableDOM: table,
    }

    // create a ghost clone
    if (!pluginState.elementClone && pluginState.startIdx !== undefined) {
      let clone = table.cloneNode() as HTMLElement
      clone.id = 'tableDragClone'
      clone.classList.add(styles.tableDragClone)

      if (pluginState.dragType === 'row') {
        // if dragging a row, clone the tr element
        const row = table.querySelector('tr:nth-child(' + (pluginState.startIdx + 1) + ')')
        if (!row) return
        clone.appendChild(row.cloneNode(true))
        meta.elementClone = clone
      } else {
        tableMap.getPosListInColumn(pluginState.startIdx).forEach((pos) => {
          const cell = view.domAtPos(pos + 1).node as HTMLElement
          const cellRow = document.createElement('tr')
          cellRow.appendChild(cell.cloneNode(true))
          clone.appendChild(cellRow)
        })
        Array.from(clone.querySelectorAll('.ProseMirror-widget')).forEach((el) => el.remove())
        meta.elementClone = clone
      }
    }
    dispatch(tr.setMeta(TableDragManagerPluginKey, meta))
  },
  100,
  { trailing: true },
)

function createTableRowDropPoint(tableEl: HTMLTableElement, dropPoint: number, tableMap: TableMap) {
  // const rowFrags = tableMap.getRowFragments() // VALID POINT - if more than one fragment, not a valid row to drop on
  const dropEl = document.createElement('div')
  const numRows = tableMap.numRows
  const tableRect = tableEl.getBoundingClientRect()
  dropEl.classList.add(styles.tableDragDropPoint)
  dropEl.style.height = '3px'
  dropEl.style.width = tableRect.width + 'px'

  const tableRowEls = tableEl.querySelectorAll('tr')
  const editorTop = document.getElementById(EDITOR_CONTAINER_ID)?.getBoundingClientRect().top ?? 0
  const scrollTop = document.getElementById(MAIN_CONTENT_ID)?.scrollTop ?? 0

  if (dropPoint === 0) {
    // drop point is drawn on top of the first row
    dropEl.style.top = tableRowEls[0].getBoundingClientRect().top - editorTop + scrollTop + 'px'
  } else if (dropPoint === numRows) {
    // drop point is drawn below the last row
    dropEl.style.top = tableRowEls[numRows - 1].getBoundingClientRect().bottom - editorTop + scrollTop + 'px'
  } else {
    // drop point is drawn on top of drop point row index
    dropEl.style.top = tableRowEls[dropPoint].getBoundingClientRect().top - editorTop + scrollTop + 'px'
  }

  return dropEl
}

function createTableColumnDropPoint(tableEl: HTMLTableElement, dropPoint: number, tableMap: TableMap) {
  const dropEl = document.createElement('div')
  // const cells = tableMap.getPosListInColumn(dropPoint)
  // const colFrags = tableMap.getColFragments() // VALID POINT - if more than one fragment, not a valid column to drop on
  const dropPointLocationInTable = tableMap
    .getColumnWidths()
    .slice(0, dropPoint)
    .reduce((a, b) => a + b, 0)
  const tableRect = tableEl.getBoundingClientRect()
  dropEl.classList.add(styles.tableDragDropPoint)
  dropEl.style.transform = `translate(-1px, 22px)`
  dropEl.style.height = tableRect.height + 'px'
  dropEl.style.width = '3px'
  dropEl.style.left = dropPointLocationInTable + 'px'

  return dropEl
}
